import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { ACTS_COMPANY } from '../config/company';
import {
  thisWeekStart, shiftWeek, weekLabel, weekRangeLabel, weekNumToMonday,
} from '../weekUtils';
import { savePdfSmart, staffInvoiceFilename, supportsFileSystemAccess, elementToPdfBlob } from '../utils/pdfSave';
import InvoiceMobile from './InvoiceMobile';
import { CheckCircle, PaperPlaneTilt, PlusCircle } from '@phosphor-icons/react';

// The invoice is meant to be one sheet. A normal week fits; a very long one
// still flows onto a second page rather than being cut.
const INVOICE_PDF = {
  scale:  0.92,
  margin: { top: '8mm', right: '8mm', bottom: '8mm', left: '8mm' },
};

/**
 * Hide everything that isn't the invoice, so the browser's Print prints the
 * document and nothing else.
 *
 * The admin reaches this screen through Reports → Invoices, which sits inside
 * the admin shell — so a browser print was capturing the sidebar, the
 * workspace switcher, the sub-tabs and the page title stacked on top of the
 * invoice. Tagging every one of those with `.no-print` is a losing game: any
 * new chrome would print again. Instead we walk from the document up to
 * <body> and hide each ancestor's other children, which leaves exactly the
 * invoice and nothing else, whatever the page is wrapped in.
 *
 * @returns {() => void} undo — always call it, print or cancel.
 */
function isolateInvoiceForPrint() {
  const el = document.getElementById('invoice-document');
  if (!el) return () => {};

  const changed = [];
  const set = (node, prop, value) => {
    changed.push([node, prop, node.style[prop]]);
    node.style[prop] = value;
  };

  for (let node = el; node && node.parentElement && node !== document.body; node = node.parentElement) {
    for (const sibling of node.parentElement.children) {
      if (sibling !== node && sibling.style.display !== 'none') {
        set(sibling, 'display', 'none');
      }
    }
    // The ancestors' own padding and max-width would otherwise squeeze the
    // document into the column it occupies on screen.
    if (node !== el) {
      set(node, 'padding', '0');
      set(node, 'margin', '0');
      set(node, 'maxWidth', 'none');
      set(node, 'width', 'auto');
    }
  }

  return () => { changed.reverse().forEach(([node, prop, prev]) => { node.style[prop] = prev; }); };
}

// PDF blob → base64 payload for the send endpoint.
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error('Could not read the generated PDF'));
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.readAsDataURL(blob);
  });
}

function fmtShort(str) {
  if (!str) return '';
  return new Date(str + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

// Column configs per contractor type
const COLS = {
  commissioned:   ['description', 'qty', 'amount'],
  hourly:         ['date', 'description', 'qty',  'rate', 'amount'],
  office_cleaner: ['date', 'description', 'qty',  'rate', 'amount'],
};

export default function InvoiceView({ user }) {
  const [weekStart, setWeekStart]       = useState(thisWeekStart());
  const [contractorId, setContractorId] = useState('');
  const [contractorList, setContractorList] = useState([]);
  const [invoice, setInvoice]           = useState(null);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  // Status now lives on the server (draft → signed → sent → approved → paid);
  // the old local `approved` flag was lost on every reload.
  const [status, setStatus] = useState({ status: 'draft' });
  const [showEmailModal, setShowEmailModal]   = useState(false);
  const [emailTo, setEmailTo]                 = useState('accounts@actscardetailing.com.au');
  const [sending, setSending]                 = useState(false);
  const [sendResult, setSendResult]           = useState(null); // { ok, msg }
  // Extra payments (Accounting Assistance, etc.)
  const [extraDesc,   setExtraDesc]   = useState('Accounting Assistance');
  const [extraAmount, setExtraAmount] = useState('');
  const [extraSaving, setExtraSaving] = useState(false);
  // Save-PDF state
  const [savingPdf,   setSavingPdf]   = useState(false);
  const [pdfBusy,     setPdfBusy]     = useState(false);
  const [pdfError,    setPdfError]    = useState('');
  // Jump-to-week controls
  const [jumpWeek, setJumpWeek]         = useState('');
  const [jumpYear, setJumpYear]         = useState(new Date().getFullYear());

  const isAdmin = user.type === 'admin';

  useEffect(() => {
    if (isAdmin) {
      api.getInvoiceContractors().then(list => {
        setContractorList(list);
        if (list.length > 0) {
          const firstId = String(list[0].id);
          setContractorId(firstId);
          // Auto-jump to the last week that has jobs for this contractor
          api.getInvoiceLastWeek(firstId).then(({ date }) => {
            if (date) setWeekStart(api.getWeekStart(date));
          }).catch(() => {});
        }
      }).catch(console.error);
    }
  }, []);

  useEffect(() => { loadInvoice(); }, [weekStart, contractorId]);

  async function loadInvoice() {
    setLoading(true); setError('');
    try {
      const params = { week: weekStart };
      if (isAdmin && contractorId) params.contractor_id = contractorId;
      const inv = await api.getInvoice(params);
      setInvoice(inv);
      setStatus({
        status: inv.status || 'draft',
        signature_png: inv.signature_png, signed_name: inv.signed_name, signed_at: inv.signed_at,
        approved_name: inv.approved_name, approved_at: inv.approved_at,
        emailed_at: inv.emailed_at, emailed_to: inv.emailed_to, paid_at: inv.paid_at,
      });
    } catch (err) { setError(err.message); setInvoice(null); setStatus({ status: 'draft' }); }
    finally { setLoading(false); }
  }

  async function handleAddExtra() {
    if (!invoice || !extraAmount || isNaN(parseFloat(extraAmount))) return;
    setExtraSaving(true);
    try {
      await api.addInvoiceExtra({
        contractor_id: invoice.contractor.id,
        week_start:    invoice.week.from,
        description:   extraDesc || 'Accounting Assistance',
        amount:        parseFloat(extraAmount),
      });
      setExtraAmount('');
      await loadInvoice();
    } catch(e) { alert('Error: ' + e.message); }
    finally { setExtraSaving(false); }
  }

  async function handleDeleteExtra(id) {
    if (!window.confirm('Remove this extra payment?')) return;
    try {
      await api.deleteInvoiceExtra(id);
      await loadInvoice();
    } catch(e) { alert('Error: ' + e.message); }
  }

  function handleSendEmail() {
    if (!invoice) return;
    setSendResult(null);
    setShowEmailModal(true);
  }

  // Contractor signs their own week. The server takes the identity from the
  // session, so nothing here can point at someone else's invoice.
  async function handleSign(signaturePng) {
    const r = await api.signInvoice(
      invoice.week_start || invoice.week.from,
      signaturePng,
      invoice.contractor.name,
      Number(invoice.total_pay || 0),
    );
    setStatus(r);
  }

  async function handleApprove(approved) {
    try {
      const r = await api.approveInvoice(invoice.contractor.id, invoice.week_start || invoice.week.from, approved);
      setStatus(r);
    } catch (e) { alert('Error: ' + e.message); }
  }

  function handlePrint() {
    const prevTitle = document.title;
    if (invoice) {
      const name = (invoice.contractor?.name || 'INVOICE').toUpperCase();
      const week = String(invoice.week_number).padStart(2, '0');
      document.title = `${name} INVOICE W${week}-${invoice.iso_year}`;
    }
    const restore = isolateInvoiceForPrint();
    const done = () => {
      restore();
      document.title = prevTitle;
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    setTimeout(() => {
      window.print();
      // Chrome fires afterprint, but not every browser does — don't leave the
      // page hidden if it doesn't.
      setTimeout(done, 1000);
    }, 100);
  }

  /**
   * The contractor's copy of the invoice, as a file they can actually keep.
   *
   * This was window.print(). In the installed app on a phone that opens
   * nothing at all, so a subcontractor had no way to get their invoice out —
   * they could sign it and then were stuck. Build the real PDF instead and
   * offer it to the share sheet first, because what they do with it is send
   * it to Michel on WhatsApp; downloading is the fallback.
   */
  async function handleContractorPdf() {
    if (!invoice || pdfBusy) return;
    setPdfBusy(true);
    setPdfError('');
    try {
      const el = document.getElementById('invoice-document');
      if (!el) throw new Error('The invoice is still loading — try again in a moment.');

      const blob = await elementToPdfBlob(el, INVOICE_PDF);
      const filename = `${invoice.invoice_number}.pdf`;
      const file = new File([blob], filename, { type: 'application/pdf' });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: filename });
          return;
        } catch (err) {
          // Dismissing the share sheet is not a failure; anything else falls
          // through to a plain download rather than leaving them empty-handed.
          if (err.name === 'AbortError') return;
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      setPdfError(`Could not build the PDF: ${err.message}`);
    } finally {
      setPdfBusy(false);
    }
  }

  async function handleConfirmSend() {
    if (!invoice) return;
    // Send it ourselves, with the PDF attached, from the company mailbox. The
    // old mailto: flow left it to each contractor's own mail app, which is why
    // invoices arrived in a different shape every week and some with no PDF.
    setSending(true);
    setSendResult(null);
    try {
      const el = document.getElementById('invoice-document');
      let pdf = null;
      if (el) {
        const blob = await elementToPdfBlob(el, INVOICE_PDF);
        pdf = { base64: await blobToBase64(blob), filename: `${invoice.invoice_number}.pdf` };
      }
      const r = await api.sendInvoiceEmail(invoice, emailTo, pdf);
      setSendResult({ ok: true, msg: r.message || `Sent to ${emailTo}` });
    } catch (err) {
      setSendResult({ ok: false, msg: `Could not send: ${err.message}. Use PRINT / PDF and send it manually.` });
    } finally {
      setSending(false);
    }
  }

  function handleJumpToWeek() {
    const wn = parseInt(jumpWeek);
    if (!wn || wn < 1 || wn > 53) return;
    setWeekStart(weekNumToMonday(jumpYear, wn));
  }

  async function handleSavePdf() {
    if (!invoice || savingPdf) return;
    setSavingPdf(true);
    try {
      const el = document.getElementById('invoice-document');
      if (!el) throw new Error('Invoice element not found');
      const name = invoice.contractor?.name || 'STAFF';
      const week = invoice.week_number;
      const year = invoice.iso_year;
      const filename = staffInvoiceFilename(name, week, year);
      const result = await savePdfSmart(el, filename, { year, week, staff: true, pdf: INVOICE_PDF });
      alert(`Invoice saved:\n${result.path}${result.fallback ? '\n(downloaded — no folder picked)' : ''}`);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      alert(`Failed to save PDF:\n${err.message || err}`);
      console.error(err);
    } finally {
      setSavingPdf(false);
    }
  }

  const navLabel = invoice
    ? weekRangeLabel(invoice.week.from, invoice.week.to)
    : weekRangeLabel(
        weekStart,
        (() => { const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() + 6); return d.toISOString().slice(0,10); })()
      );

  const contractorType = invoice?.contractor?.type || user.type;
  const cols           = COLS[contractorType] || COLS.commissioned;

  // The printable document. The contractor screen keeps it mounted but
  // hidden (`print-only`) so Print / PDF still captures the real invoice.
  const paper = !invoice ? null : (
          <div id="invoice-document" className="invoice-document-wrap" style={{
            background: 'white', borderRadius: 0,
            padding: '26px 30px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            color: '#1a1a2e',
          }}>

            {/* ── HEADER: contractor info left, INVOICE title right ── */}
            <div className="invoice-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, paddingBottom: 9, borderBottom: '2.5px solid #1A1A1A' }}>

              {/* Left: contractor details */}
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#1A1A1A', marginBottom: 2 }}>
                  {invoice.contractor.name}
                </div>
                {invoice.contractor.abn ? (
                  <div style={{ fontSize: 13, color: '#374151', marginBottom: 2 }}>
                    <strong>ABN:</strong> {invoice.contractor.abn}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>ABN not set — add in My Account</div>
                )}
                {invoice.contractor.email && (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{invoice.contractor.email}</div>
                )}
                {invoice.contractor.phone && (
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{invoice.contractor.phone}</div>
                )}
              </div>

              {/* Right: INVOICE label + number + week */}
              <div className="invoice-header-right" style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#4A7A91', letterSpacing: -1 }}>INVOICE</div>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>
                  <strong>{invoice.invoice_number}</strong>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  <span style={{ background: '#F5C518', color: '#1A1A1A', fontWeight: 700, padding: '2px 8px', borderRadius: 0, fontSize: 11 }}>
                    Week {invoice.week_number} / {invoice.iso_year}
                  </span>
                  <span style={{ marginLeft: 6, color: '#9ca3af', fontSize: 11 }}>FY Week {invoice.fy_week}</span>
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  {fmtShort(invoice.week.from)} → {fmtShort(invoice.week.to)}
                </div>
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                  Issued: {new Date().toLocaleDateString('en-AU')}
                </div>
              </div>
            </div>

            {/* ── BILL TO ── */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 }}>
                Bill To
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1A1A' }}>{ACTS_COMPANY.name}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>ABN: {ACTS_COMPANY.abn}</div>
              <div style={{ fontSize: 13, color: '#6b7280' }}>{ACTS_COMPANY.address}</div>
            </div>

            {/* ── LINE ITEMS TABLE ── */}
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 10 }}>
              <thead>
                <tr style={{ background: '#f4f8fa', borderBottom: '2px solid #e5e7eb' }}>
                  {cols.includes('date') && (
                    <th style={thStyle}>Date</th>
                  )}
                  {cols.includes('description') && (
                    <th style={{ ...thStyle, textAlign: 'left', width: '40%' }}>Description</th>
                  )}
                  {cols.includes('ref') && (
                    <th style={thStyle}>Stock #</th>
                  )}
                  {cols.includes('hours') && (
                    <th style={{ ...thStyle, textAlign: 'right' }}>Hours</th>
                  )}
                  {cols.includes('qty') && (
                    <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                  )}
                  {cols.includes('rate') && (
                    <th style={{ ...thStyle, textAlign: 'right' }}>Rate</th>
                  )}
                  <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.line_items.length === 0 ? (
                  <tr>
                    <td colSpan={cols.length + 1} style={{ textAlign: 'center', padding: '28px 16px', color: '#9ca3af', fontSize: 14 }}>
                      {contractorType === 'office_cleaner'
                        ? 'No completed clock records for this week.'
                        : 'No approved jobs found for this week.'}
                    </td>
                  </tr>
                ) : invoice.line_items.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f3f4f6', background: item.pending ? '#fffbeb' : 'transparent', opacity: item.pending ? 0.8 : 1 }}>
                    {cols.includes('date') && (
                      <td style={{ ...tdStyle, color: '#6b7280', whiteSpace: 'nowrap', fontSize: 12 }}>
                        {fmtShort(item.date)}
                      </td>
                    )}
                    {cols.includes('description') && (
                      <td style={{ ...tdStyle, fontWeight: 500 }}>
                        {item.description}
                        {item.pending && <span style={{ marginLeft: 6, fontSize: 10, background: '#fde68a', color: '#92400e', borderRadius: 0, padding: '1px 5px', fontWeight: 700 }}>PENDING</span>}
                      </td>
                    )}
                    {cols.includes('ref') && (
                      <td style={{ ...tdStyle, textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                        {item.job_ref || '—'}
                      </td>
                    )}
                    {cols.includes('hours') && (
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                        {item.qty > 0 ? `${Number(item.qty).toFixed(2)} h` : '—'}
                      </td>
                    )}
                    {cols.includes('qty') && (
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {item.qty_label || item.qty}
                      </td>
                    )}
                    {cols.includes('rate') && (
                      <td style={{ ...tdStyle, textAlign: 'right', fontSize: 13 }}>
                        {item.pending ? '—' : `$${Number(item.rate || 0).toFixed(2)}`}
                      </td>
                    )}
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: item.pending ? '#9ca3af' : 'inherit' }}>
                      {item.pending ? <span style={{ fontSize: 12, fontStyle: 'italic' }}>${Number(item.pending_pay || 0).toFixed(2)} pending</span> : `$${Number(item.pay || 0).toFixed(2)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── TOTALS ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
              <div style={{ minWidth: 260, borderTop: '2px solid #1A1A1A', paddingTop: 8 }}>
                {invoice.total_pending > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#92400e', marginBottom: 6, background: '#fef3c7', borderRadius: 0, padding: '4px 8px' }}>
                    <span>⏳ Pending approval</span>
                    <span>${Number(invoice.total_pending || 0).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#6b7280', marginBottom: 5 }}>
                  <span>Approved subtotal</span>
                  <span>${Number(invoice.total_pay || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 800, borderTop: '1px solid #e5e7eb', paddingTop: 7 }}>
                  <span>TOTAL DUE</span>
                  <span style={{ color: '#16a34a' }}>${Number(invoice.total_pay || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* ── FOOTER BLOCKS: payment details + who signed / approved ── */}
            <div className="invoice-footer-blocks" style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ background: '#f4f8fa', borderRadius: 0, padding: '11px 14px', borderLeft: '3px solid #4A7A91' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#4A7A91', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 7 }}>
                Payment Details
              </div>
              {invoice.contractor.bsb ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 16px', fontSize: 12.5 }}>
                  <span style={{ color: '#6b7280' }}>Account Name</span>
                  <span style={{ fontWeight: 700 }}>{invoice.contractor.name}</span>
                  <span style={{ color: '#6b7280' }}>BSB</span>
                  <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{invoice.contractor.bsb}</span>
                  <span style={{ color: '#6b7280' }}>Account Number</span>
                  <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{invoice.contractor.account_number || '—'}</span>
                  {invoice.contractor.abn && (
                    <>
                      <span style={{ color: '#6b7280' }}>ABN</span>
                      <span style={{ fontWeight: 600 }}>{invoice.contractor.abn}</span>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>
                  Bank details not provided. Please update in My Account before generating an invoice.
                </div>
              )}
            </div>
            </div>

            {/* Signed by the contractor, approved by the admin — so Michel is
                always approving against a document that carries the signature. */}
            <div style={{ width: 190, flex: 'none', background: '#f7f9fa', borderRadius: 4, padding: '10px 12px' }}>
              {status.signed_at && status.signature_png ? (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9ca3af' }}>Signed by</div>
                  <img src={status.signature_png} alt="Contractor signature" style={{ maxHeight: 44, maxWidth: '100%', display: 'block', margin: '6px 0 2px' }} />
                  <div style={{ borderTop: '1px solid #dfe3e7', paddingTop: 5, fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>
                    {status.signed_name || invoice.contractor.name}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {new Date(status.signed_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9ca3af' }}>Signed by</div>
                  <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic', marginTop: 6 }}>Not signed yet</div>
                </>
              )}

              {status.approved_at && (
                <div style={{ marginTop: 12, borderTop: '1px solid #dfe3e7', paddingTop: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9ca3af' }}>Approved by</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginTop: 3 }}>{status.approved_name || 'Admin'}</div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {new Date(status.approved_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                  </div>
                </div>
              )}

              <div style={{ fontSize: 10.5, color: '#9ca3af', marginTop: 12, lineHeight: 1.45 }}>
                Recipient-created tax invoice issued by ACT's Car Detailing.
              </div>
            </div>
            </div>

            {/* ── FOOTER ── */}
            <div style={{ marginTop: 9, paddingTop: 6, borderTop: '1px solid #f3f4f6', fontSize: 9.5, color: '#9ca3af', textAlign: 'center' }}>
              Invoice generated {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
  );

  // ── Contractor: their own invoice, on a phone. No picker, no editing —
  // they check the week, sign, and it lands with Michel.
  if (!isAdmin) {
    return (
      <>
        <InvoiceMobile
          invoice={invoice}
          status={status}
          loading={loading}
          error={error}
          isThisWeek={weekStart === thisWeekStart()}
          onPrevWeek={() => setWeekStart(shiftWeek(weekStart, -1))}
          onNextWeek={() => setWeekStart(shiftWeek(weekStart, 1))}
          onThisWeek={() => setWeekStart(thisWeekStart())}
          onSign={handleSign}
          onPdf={handleContractorPdf}
          pdfBusy={pdfBusy}
          pdfError={pdfError}
        />
        <div className="print-only">{paper}</div>
      </>
    );
  }

  // ── Admin desktop: the paper on the left, one control rail on the right.
  // The old screen had four same-weight buttons in four colours above the
  // document; the rail gives each state exactly one primary action.
  const st        = status.status || 'draft';
  const approved  = st === 'approved' || st === 'paid';
  const signedOk  = !!status.signed_at;
  const banner    = approved
    ? { bg: '#dcfce7', fg: '#15803d', title: 'Approved', sub: 'Ready to send' }
    : signedOk
      ? { bg: '#fef6da', fg: '#8a6410', title: 'Signed by contractor', sub: 'Waiting for your approval' }
      : { bg: '#fef6da', fg: '#8a6410', title: 'Not signed yet', sub: 'The contractor has not signed this week' };

  const railCard  = { background: '#fff', borderRadius: 6, boxShadow: '0 1px 4px rgba(0,0,0,.06)', padding: 16 };
  const railLbl   = { fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9ca3af', marginBottom: 6 };
  const secondary = { flex: 1, height: 38, borderRadius: 4, border: '1.5px solid #dfe3e7', background: '#fff', fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' };
  const railInput = { height: 42, padding: '0 10px', border: '1.5px solid #dfe3e7', borderRadius: 4, fontSize: 13.5, boxSizing: 'border-box', width: '100%' };

  return (
    <div style={{ background: '#eceef0', minHeight: '100%', padding: '28px 32px 56px', boxSizing: 'border-box' }}>
      <div className="invoice-admin-grid" style={{ display: 'flex', gap: 24, alignItems: 'flex-start', maxWidth: 1180, margin: '0 auto' }}>

        {/* ── Left: title + the document ─────────────────────────────── */}
        <div style={{ flex: 1, minWidth: 0, maxWidth: 820 }}>
          <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1a1a2e' }}>Invoice Generator</h2>
          <div className="no-print" style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 18px' }}>
            Contractor invoices are built from approved jobs for the selected week.
          </div>

          {error   && <div className="alert alert-error no-print">{error}</div>}
          {loading && <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>}

          {paper}
        </div>

        {/* ── Right: the control rail ────────────────────────────────── */}
        <div className="no-print invoice-rail" style={{ width: 300, flex: 'none', position: 'sticky', top: 28, display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Card 1 — status + actions */}
          <div style={{ ...railCard, borderTop: '3px solid #F5C518' }}>
            <div style={{ background: banner.bg, color: banner.fg, borderRadius: 4, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <CheckCircle size={20} weight="fill" style={{ flex: 'none' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800 }}>{banner.title}</div>
                <div style={{ fontSize: 11.5, opacity: 0.85 }}>{banner.sub}</div>
              </div>
              {invoice && (
                <button
                  onClick={() => handleApprove(!approved)}
                  style={{ background: 'none', border: 'none', padding: 0, fontSize: 11.5, fontWeight: 700, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', flex: 'none' }}
                >
                  {approved ? 'Undo' : 'Approve'}
                </button>
              )}
            </div>

            <button
              onClick={handleSendEmail}
              disabled={!invoice || !approved}
              style={{ width: '100%', height: 42, borderRadius: 4, border: 'none', fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: (invoice && approved) ? '#4A7A91' : '#e5e7eb', color: (invoice && approved) ? '#fff' : '#9ca3af',
                cursor: (invoice && approved) ? 'pointer' : 'not-allowed' }}
            >
              <PaperPlaneTilt size={17} weight="fill" /> Send to Accounts
            </button>

            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={handleSavePdf} disabled={!invoice || savingPdf} style={secondary}
                title={supportsFileSystemAccess() ? 'Save PDF directly to your reports folder' : 'Save PDF to Downloads'}>
                {savingPdf ? 'Saving…' : 'Save PDF'}
              </button>
              <button onClick={handlePrint} disabled={!invoice} style={secondary}>Print</button>
            </div>

            <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 10 }}>
              {status.emailed_at
                ? `Sent to ${status.emailed_to || 'accounts'} · ${new Date(status.emailed_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}`
                : 'Not sent yet'}
            </div>
          </div>

          {/* Card 2 — contractor + week */}
          <div style={railCard}>
            {contractorList.length > 0 && (
              <>
                <div style={railLbl}>Contractor</div>
                <select
                  value={contractorId}
                  onChange={e => {
                    const newId = e.target.value;
                    setContractorId(newId);
                    // Jump to the last week that actually has jobs for them.
                    api.getInvoiceLastWeek(newId).then(({ date }) => {
                      if (date) setWeekStart(api.getWeekStart(date));
                    }).catch(() => {});
                  }}
                  style={railInput}
                >
                  {contractorList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </>
            )}

            <div style={{ ...railLbl, marginTop: 14 }}>Billing week</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
              <button onClick={() => setWeekStart(shiftWeek(weekStart, -1))} style={{ ...secondary, flex: 'none', width: 44, height: 'auto', fontSize: 18 }}>‹</button>
              <div style={{ flex: 1, minWidth: 0, background: '#f7f9fa', borderRadius: 4, padding: '7px 6px', textAlign: 'center', lineHeight: 1.25 }}>
                <div style={{ fontSize: 13.5, fontWeight: 800, color: '#1a1a2e' }}>
                  {invoice ? `Week ${invoice.week_number} · ${invoice.iso_year}` : weekLabel(weekStart)}
                </div>
                <div style={{ fontSize: 11.5, color: '#6b7280' }}>{navLabel}</div>
              </div>
              <button onClick={() => setWeekStart(shiftWeek(weekStart, 1))} style={{ ...secondary, flex: 'none', width: 44, height: 'auto', fontSize: 18 }}>›</button>
            </div>

            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <input type="number" min="1" max="53" placeholder="Wk" value={jumpWeek}
                onChange={e => setJumpWeek(e.target.value)} style={{ ...railInput, flex: 1, minWidth: 0, height: 38 }} />
              <input type="number" min="2020" max="2099" placeholder="Year" value={jumpYear}
                onChange={e => setJumpYear(parseInt(e.target.value))} style={{ ...railInput, flex: 1, minWidth: 0, height: 38 }} />
              <button onClick={handleJumpToWeek} style={{ ...secondary, flex: 'none', width: 42 }}>Go</button>
            </div>
            <button onClick={() => setWeekStart(thisWeekStart())} style={{ ...secondary, width: '100%', marginTop: 6 }}>This week</button>
          </div>

          {/* Card 3 — extra payments. Not part of the printed document, so it
              belongs in the rail rather than on the paper. */}
          {invoice && (
            <div style={railCard}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                <PlusCircle size={18} color="#4A7A91" weight="fill" />
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e' }}>Add extra payment</span>
              </div>
              <div style={{ fontSize: 11.5, color: '#9ca3af', marginBottom: 10 }}>
                Accounting assistance, bonus, reimbursement.
              </div>

              {invoice.line_items.filter(l => l.is_extra).map(ex => (
                <div key={ex.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, background: '#f7f9fa', borderRadius: 4, padding: '7px 10px' }}>
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: '#374151' }}>{ex.description}</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>${Number(ex.pay).toFixed(2)}</span>
                  <button onClick={() => handleDeleteExtra(ex.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
                </div>
              ))}

              <input value={extraDesc} onChange={e => setExtraDesc(e.target.value)}
                placeholder="Description" style={{ ...railInput, height: 38, marginTop: 4 }} />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <input type="number" min="0" step="0.01" value={extraAmount}
                  onChange={e => setExtraAmount(e.target.value)} placeholder="$ 0.00"
                  style={{ ...railInput, flex: 1, minWidth: 0, height: 38 }} />
                <button onClick={handleAddExtra} disabled={extraSaving || !extraAmount}
                  style={{ ...secondary, flex: 'none', width: 64, borderColor: extraAmount ? '#4A7A91' : '#dfe3e7', color: extraAmount ? '#4A7A91' : '#9ca3af' }}>
                  {extraSaving ? '…' : 'Add'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── EMAIL PREVIEW MODAL ─────────────────────────────────── */}
      {showEmailModal && invoice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto' }}>
          <div style={{ background: '#fff', borderRadius: 0, width: '100%', maxWidth: 700, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>

            {/* Modal header */}
            <div style={{ padding: '20px 24px 16px', borderBottom: '1.5px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Preview do Email</h3>
              <button onClick={() => { setShowEmailModal(false); setSendResult(null); }} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
            </div>

            {/* Email fields */}
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f3f4f6', background: '#f9fafb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: '8px 12px', alignItems: 'center', fontSize: 13 }}>
                <span style={{ color: '#6b7280', fontWeight: 600 }}>Para:</span>
                <input
                  value={emailTo}
                  onChange={e => setEmailTo(e.target.value)}
                  style={{ padding: '6px 10px', border: '1.5px solid #d1d5db', borderRadius: 0, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                />
                <span style={{ color: '#6b7280', fontWeight: 600 }}>Assunto:</span>
                <span style={{ fontWeight: 500, color: '#374151' }}>
                  Invoice {invoice.invoice_number} — {invoice.contractor.name} — Week {invoice.week_number}/{invoice.iso_year}
                </span>
              </div>
            </div>

            {/* HTML Preview */}
            <div style={{ padding: '16px 24px', maxHeight: 460, overflowY: 'auto', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>Prévia do conteúdo</div>

              {/* Mini invoice preview */}
              <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 0, overflow: 'hidden', fontSize: 13 }}>
                {/* Header */}
                <div style={{ background: '#fff', padding: '20px 24px 16px', borderBottom: '2px solid #1A1A1A' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: '#1A1A1A' }}>{invoice.contractor.name}</div>
                      {invoice.contractor.abn && <div style={{ fontSize: 12, color: '#374151' }}><strong>ABN:</strong> {invoice.contractor.abn}</div>}
                      {invoice.contractor.email && <div style={{ fontSize: 12, color: '#6b7280' }}>{invoice.contractor.email}</div>}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 22, fontWeight: 900, color: '#4A7A91' }}>INVOICE</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{invoice.invoice_number}</div>
                      <div style={{ marginTop: 4 }}>
                        <span style={{ background: '#F5C518', color: '#1A1A1A', fontWeight: 700, padding: '2px 7px', borderRadius: 0, fontSize: 10 }}>Week {invoice.week_number}/{invoice.iso_year}</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bill To */}
                <div style={{ padding: '12px 24px', background: '#fff' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>Bill To</div>
                  <div style={{ fontWeight: 700, color: '#1A1A1A' }}>{ACTS_COMPANY.name}</div>
                  <div style={{ color: '#6b7280', fontSize: 12 }}>ABN: {ACTS_COMPANY.abn}</div>
                  <div style={{ color: '#6b7280', fontSize: 12 }}>{ACTS_COMPANY.address}</div>
                </div>
                {/* Line items summary */}
                <div style={{ padding: '0 24px 12px', background: '#fff' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: '#f4f8fa' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Date</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Description</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', fontSize: 10 }}>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoice.line_items.filter(l => !l.pending).map((item, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 8px', color: '#6b7280', whiteSpace: 'nowrap' }}>{fmtShort(item.date)}</td>
                          <td style={{ padding: '6px 8px' }}>{item.description}{item.job_ref ? ` — ${item.job_ref}` : ''}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700 }}>${Number(item.pay || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Total */}
                <div style={{ padding: '8px 24px 16px', background: '#f9fafb', display: 'flex', justifyContent: 'flex-end' }}>
                  <div style={{ minWidth: 220, borderTop: '2px solid #1A1A1A', paddingTop: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800 }}>
                      <span>TOTAL DUE</span>
                      <span style={{ color: '#16a34a' }}>${Number(invoice.total_pay || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
                {/* Payment */}
                {invoice.contractor.bsb && (
                  <div style={{ padding: '12px 24px 16px', background: '#f4f8fa', borderLeft: '4px solid #4A7A91', margin: '0 24px 16px', borderRadius: 0 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#4A7A91', textTransform: 'uppercase', marginBottom: 8 }}>Payment Details</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: 12 }}>
                      <span style={{ color: '#6b7280' }}>Account Name</span><span style={{ fontWeight: 700 }}>{invoice.contractor.name}</span>
                      <span style={{ color: '#6b7280' }}>BSB</span><span style={{ fontWeight: 700 }}>{invoice.contractor.bsb}</span>
                      <span style={{ color: '#6b7280' }}>Account #</span><span style={{ fontWeight: 700 }}>{invoice.contractor.account_number || '—'}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Send result */}
            {sendResult && (
              <div style={{ padding: '10px 24px', background: sendResult.ok ? '#dcfce7' : '#fef2f2', color: sendResult.ok ? '#15803d' : '#dc2626', fontSize: 13, fontWeight: 600 }}>
                {sendResult.ok ? '✅ ' : '❌ '}{sendResult.msg}
              </div>
            )}

            {/* Actions */}
            <div style={{ padding: '16px 24px', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={() => { setShowEmailModal(false); setSendResult(null); }}
                style={{ padding: '8px 18px', borderRadius: 0, border: '1.5px solid #d1d5db', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}
              >
                Cancelar
              </button>
              {sendResult?.ok ? (
                <button
                  onClick={() => { setShowEmailModal(false); setSendResult(null); }}
                  style={{ padding: '8px 18px', borderRadius: 0, border: 'none', background: '#16a34a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                >
                  ✅ Fechar
                </button>
              ) : (
                <button
                  onClick={handleConfirmSend}
                  disabled={sending}
                  style={{ padding: '8px 20px', borderRadius: 0, border: 'none', background: sending ? '#9ca3af' : '#4A7A91', color: '#fff', fontSize: 13, fontWeight: 700, cursor: sending ? 'wait' : 'pointer' }}
                >
                  {sending ? 'Gerando PDF e enviando…' : '📧 Enviar com PDF'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        /* Narrow admin windows: the rail drops under the paper instead of
           squeezing it. */
        @media (max-width: 1080px) {
          .invoice-admin-grid { flex-direction: column; }
          .invoice-rail { width: 100% !important; position: static !important; }
        }
        @media print {
          .no-print { display: none !important; }
          header, nav { display: none !important; }
          body { background: white !important; margin: 0 !important; }
          #invoice-document {
            box-shadow: none !important;
            border-radius: 0 !important;
            /* Puppeteer already applies a 10mm page margin — stacking another
               18mm here is what pushed a normal week onto a second sheet. */
            padding: 2mm 4mm !important;
            max-width: 100% !important;
          }
          /* Keep the money blocks whole; only the line-item rows may break. */
          .invoice-footer-blocks { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

const thStyle = {
  padding: '6px 10px', fontSize: 10, fontWeight: 700,
  color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5,
  textAlign: 'left',
};
const tdStyle = {
  padding: '4px 10px', fontSize: 12, verticalAlign: 'middle',
};
